import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Scissors, Truck, Sprout, ArrowRight, Leaf, Ruler } from 'lucide-react';
import { storeApi } from '../../lib/api';
import { VarietyCard } from './VarietyCard';
import { DeliveryChecker } from './DeliveryChecker';
import { AreaCalculatorDialog } from './area/AreaCalculatorDialog';

const HERO_IMG = 'https://customer-assets-eiarnc6j.emergentagent.net/job_field-mapper-24/artifacts/15163a04071888c1_20260917_172755.jpg';

const PERKS = [
  { icon: Scissors, title: 'Cut fresh to order', text: 'We harvest your turf the morning it leaves the farm so it arrives green, moist and ready to lay.' },
  { icon: Truck, title: 'Farm-direct delivery', text: 'Delivery across South East Queensland or collect from the farm.' },
  { icon: Sprout, title: 'Grown by turf people', text: 'A family farm with decades of experience. Ask us which variety suits your yard and we will tell you straight.' },
];

const STEPS = [
  { title: 'Pick your variety', text: 'Browse our range below and compare shade, drought and wear tolerance.' },
  { title: 'Tell us your area', text: 'Use our lawn calculator to sketch your shape, enter your suburb, and see turf and delivery pricing before you submit.' },
  { title: 'We confirm & deliver', text: 'We will call to confirm your date and share our lay-day tips.' },
];

export default function StoreHomePage() {
  const [varieties, setVarieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calcOpen, setCalcOpen] = useState(false);

  useEffect(() => {
    storeApi.getVarieties().then((r) => setVarieties(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="store-home">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'var(--s-green)' }}>
        <img src={HERO_IMG} alt="Sunset over the turf paddocks at Greenhill Creek Turf" className="absolute inset-0 w-full h-full object-cover object-[center_60%]" data-testid="hero-image" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(20,45,34,0.82) 0%, rgba(20,45,34,0.45) 40%, rgba(20,45,34,0.05) 70%, rgba(20,45,34,0) 100%)' }} />
        <div className="absolute inset-x-0 bottom-0 h-40" style={{ background: 'linear-gradient(180deg, rgba(20,45,34,0) 0%, rgba(20,45,34,0.45) 100%)' }} />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-36">
          <div className="max-w-2xl text-white" style={{ textShadow: '0 2px 18px rgba(0,0,0,0.45)' }}>
            <p className="store-eyebrow store-rise mb-5">Greenhill Creek Turf · Queensland</p>
            <h1 className="store-rise store-rise-1 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
              Fresh turf, cut the morning it lands in your yard.
            </h1>
            <p className="store-rise store-rise-2 text-base md:text-lg text-white/80 leading-relaxed mb-10 max-w-xl">
              Premium buffalo, zoysia, couch and kikuyu grown on our family farm. Browse the range, tell us your square metres and we will take care of the rest.
            </p>
            <div className="store-rise store-rise-3 flex flex-wrap gap-3">
              <a href="#varieties" className="store-btn store-btn-primary" data-testid="hero-browse-btn">Browse varieties <ArrowRight className="h-4 w-4" /></a>
              <Link to="/shop/order" className="store-btn store-btn-ghost" data-testid="hero-order-btn">Start an order</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 -mt-10 relative z-10">
        <div className="grid md:grid-cols-3 gap-4">
          {PERKS.map((p, i) => (
            <div key={p.title} className={`bg-white rounded-2xl p-6 flex gap-4 shadow-lg shadow-black/5 store-rise store-rise-${i + 1}`} data-testid={`perk-${i}`}>
              <span className="w-11 h-11 shrink-0 rounded-full flex items-center justify-center" style={{ background: 'var(--s-sand)', color: 'var(--s-green)' }}><p.icon className="h-5 w-5" /></span>
              <div>
                <h3 className="font-heading font-bold mb-1">{p.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--s-muted)' }}>{p.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Varieties */}
      <section id="varieties" className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28" data-testid="varieties-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <div>
            <p className="store-eyebrow mb-3">Our range</p>
            <h2 className="text-3xl sm:text-4xl font-bold leading-tight">Turf varieties grown on the farm</h2>
          </div>
          <div className="max-w-md">
            <p className="text-sm md:text-base leading-relaxed mb-3" style={{ color: 'var(--s-muted)' }}>
              Every lawn is different. Compare shade, drought and wear tolerance, or give us a call and we will point you to the right one.
            </p>
            <button type="button" onClick={() => setCalcOpen(true)} className="store-btn store-btn-outline py-2.5" data-testid="home-area-calculator-btn"><Ruler className="h-4 w-4" /> Not sure how much? Measure your lawn</button>
          </div>
        </div>
        <AreaCalculatorDialog open={calcOpen} onOpenChange={setCalcOpen} onUse={() => {}} varieties={varieties} />

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => <div key={i} className="h-96 rounded-2xl animate-pulse" style={{ background: 'var(--s-sand)' }} />)}
          </div>
        ) : varieties.length === 0 ? (
          <div className="text-center py-20 rounded-2xl" style={{ background: 'var(--s-sand)' }} data-testid="no-varieties">
            <Leaf className="h-10 w-10 mx-auto mb-3" style={{ color: 'var(--s-green)' }} />
            <p className="font-heading font-semibold">Our range is being updated. Check back soon.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="varieties-grid">
            {varieties.map((v, i) => <VarietyCard key={v.id} variety={v} index={i} />)}
          </div>
        )}
      </section>

      <DeliveryChecker />

      {/* How it works */}
      <section className="py-20 lg:py-28" style={{ background: 'var(--s-green)' }} data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-white">
          <p className="store-eyebrow mb-3">How ordering works</p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-14 max-w-xl leading-tight">Three steps from paddock to your place</h2>
          <div className="store-steps grid md:grid-cols-3 gap-10">
            {STEPS.map((s) => (
              <div key={s.title} className="store-step">
                <h3 className="font-heading font-bold text-xl mb-2">{s.title}</h3>
                <p className="text-white/70 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-14">
            <Link to="/shop/order" className="store-btn store-btn-primary" data-testid="how-order-btn">Place your order <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
